import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, RotateCcw, Trophy } from "lucide-react";
import { didimQuizData, quizConfig } from "./quizData";
import "./DidimQuiz.css";

const DidimQuiz = ({ isOpen, onClose }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(quizConfig.timePerQuestion);
  const [isAnswered, setIsAnswered] = useState(false);

  // Quiz başlatma - rastgele 5 soru seç
  useEffect(() => {
    if (isOpen && quizQuestions.length === 0) {
      const shuffled = [...didimQuizData].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, quizConfig.questionsPerQuiz);
      setQuizQuestions(selected);
      resetQuiz();
    }
  }, [isOpen]);

  // Zamanlayıcı
  useEffect(() => {
    let timer;
    if (isOpen && !showResult && !isAnswered && timeLeft > 0) {
      timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (timeLeft === 0 && !isAnswered) {
      handleTimeout();
    }
    return () => clearTimeout(timer);
  }, [timeLeft, isOpen, showResult, isAnswered]);

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
    setShowResult(false);
    setAnswers([]);
    setTimeLeft(quizConfig.timePerQuestion);
    setIsAnswered(false);
  };

  const handleTimeout = () => {
    setIsAnswered(true);
    setAnswers([
      ...answers,
      {
        questionId: quizQuestions[currentQuestion].id,
        selected: null,
        correct: false,
      },
    ]);
    setTimeout(nextQuestion, 2000);
  };

  const handleAnswerSelect = (answerIndex) => {
    if (isAnswered) return;

    setSelectedAnswer(answerIndex);
    setIsAnswered(true);

    const isCorrect =
      answerIndex === quizQuestions[currentQuestion].correctAnswer;
    if (isCorrect) {
      setScore(score + 1);
    }

    setAnswers([
      ...answers,
      {
        questionId: quizQuestions[currentQuestion].id,
        selected: answerIndex,
        correct: isCorrect,
      },
    ]);

    setTimeout(nextQuestion, 2000);
  };

  const nextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setTimeLeft(quizConfig.timePerQuestion);
      setIsAnswered(false);
    } else {
      setShowResult(true);
    }
  };

  const restartQuiz = () => {
    const shuffled = [...didimQuizData].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, quizConfig.questionsPerQuiz);
    setQuizQuestions(selected);
    resetQuiz();
  };

  const closeQuiz = () => {
    resetQuiz();
    setQuizQuestions([]);
    onClose();
  };

  const getScoreMessage = () => {
    const percentage = (score / quizConfig.questionsPerQuiz) * 100;
    if (percentage >= 90) return "Mükemmel! Didim uzmanısın! 🏆";
    if (percentage >= 70)
      return "Harika! Didim hakkında çok şey biliyorsun! 🌟";
    if (percentage >= 50) return "İyi! Didim'i keşfetmeye devam et! 👍";
    return "Didim'i daha iyi tanımak için ziyaret et! 🌊";
  };

  const getScoreColor = () => {
    const percentage = (score / quizConfig.questionsPerQuiz) * 100;
    if (percentage >= 90) return "#FFD700";
    if (percentage >= 70) return "#00C851";
    if (percentage >= 50) return "#FF8800";
    return "#FF4444";
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="quiz-overlay"
        onClick={closeQuiz}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="quiz-container"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Wave decorations */}
          <div className="quiz-waves">
            <div className="wave wave-1"></div>
            <div className="wave wave-2"></div>
            <div className="wave wave-3"></div>
          </div>

          {/* Close button */}
          <button className="quiz-close" onClick={closeQuiz}>
            <X size={24} />
          </button>

          {!showResult ? (
            <>
              {/* Header */}
              <div className="quiz-header">
                <div className="quiz-title">
                  <h2>🏛️ Didim Bilgi Yarışması</h2>
                  <p>Didim hakkında ne kadar bilgilisin?</p>
                </div>

                {/* Progress */}
                <div className="quiz-progress">
                  <div className="progress-bar">
                    <motion.div
                      className="progress-fill"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${
                          ((currentQuestion + 1) /
                            quizConfig.questionsPerQuiz) *
                          100
                        }%`,
                      }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <span className="progress-text">
                    {currentQuestion + 1} / {quizConfig.questionsPerQuiz}
                  </span>
                </div>

                {/* Timer */}
                <div className="quiz-timer">
                  <div className="timer-circle">
                    <svg viewBox="0 0 36 36" className="timer-svg">
                      <path
                        className="timer-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <motion.path
                        className="timer-progress"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        initial={{ strokeDasharray: "100 100" }}
                        animate={{
                          strokeDasharray: `${
                            (timeLeft / quizConfig.timePerQuestion) * 100
                          } 100`,
                        }}
                        transition={{ duration: 0.5 }}
                      />
                    </svg>
                    <span className="timer-text">{timeLeft}</span>
                  </div>
                </div>
              </div>

              {/* Question */}
              {quizQuestions.length > 0 && (
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="quiz-question"
                >
                  <h3>{quizQuestions[currentQuestion].question}</h3>

                  <div className="quiz-options">
                    {quizQuestions[currentQuestion].options.map(
                      (option, index) => (
                        <motion.button
                          key={index}
                          className={`quiz-option ${
                            selectedAnswer === index ? "selected" : ""
                          } ${
                            isAnswered &&
                            index ===
                              quizQuestions[currentQuestion].correctAnswer
                              ? "correct"
                              : isAnswered &&
                                selectedAnswer === index &&
                                index !==
                                  quizQuestions[currentQuestion].correctAnswer
                              ? "incorrect"
                              : ""
                          }`}
                          onClick={() => handleAnswerSelect(index)}
                          disabled={isAnswered}
                          whileHover={{ scale: isAnswered ? 1 : 1.02 }}
                          whileTap={{ scale: isAnswered ? 1 : 0.98 }}
                        >
                          <span className="option-letter">
                            {String.fromCharCode(65 + index)}
                          </span>
                          <span className="option-text">{option}</span>
                          <ChevronRight size={20} className="option-arrow" />
                        </motion.button>
                      )
                    )}
                  </div>
                </motion.div>
              )}
            </>
          ) : (
            /* Results */
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="quiz-results"
            >
              <div className="results-header">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  className="results-icon"
                >
                  <Trophy size={48} style={{ color: getScoreColor() }} />
                </motion.div>
                <h2>Quiz Tamamlandı!</h2>
                <p className="results-message">{getScoreMessage()}</p>
              </div>

              <div className="results-score">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                  className="score-circle"
                  style={{ borderColor: getScoreColor() }}
                >
                  <span
                    className="score-number"
                    style={{ color: getScoreColor() }}
                  >
                    {score}
                  </span>
                  <span className="score-total">
                    / {quizConfig.questionsPerQuiz}
                  </span>
                </motion.div>
                <div className="score-percentage">
                  <span style={{ color: getScoreColor() }}>
                    %{Math.round((score / quizConfig.questionsPerQuiz) * 100)}
                  </span>
                </div>
              </div>

              <div className="results-details">
                <div className="detail-item">
                  <span className="detail-label">Doğru Cevaplar:</span>
                  <span className="detail-value correct">{score}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Yanlış Cevaplar:</span>
                  <span className="detail-value incorrect">
                    {quizConfig.questionsPerQuiz - score}
                  </span>
                </div>
              </div>

              <div className="results-actions">
                <motion.button
                  className="btn-restart"
                  onClick={restartQuiz}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <RotateCcw size={20} />
                  <span>Tekrar Oyna</span>
                </motion.button>
                <motion.button
                  className="btn-close"
                  onClick={closeQuiz}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Kapat</span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DidimQuiz;
